<template>
  <v-card variant="flat" class="mt-3">
    <v-card-title class="text-center pt-6 pb-8" v-if="display">
      <div class="d-flex align-center justify-center">
        <v-icon size="x-small" class="mr-2" :icon="display.icon"></v-icon>
        <span>{{ display.label }}</span>
        <v-chip size="small" color="disabled" class="ml-2 font-weight-medium">
          {{ display.chip }}
        </v-chip>
      </div>
      <div v-if="display.subIcon && display.subLabel" class="d-flex align-center justify-center">
        <v-icon size="x-small" class="mr-2" :icon="display.subIcon"></v-icon>
        <span>{{ display.subLabel }}</span>
      </div>
      <v-btn
        class="mt-4"
        color="primary"
        :disabled="display.button.disabled"
        :append-icon="display.button.appendIcon">
        {{ display.button.label }}
      </v-btn>
    </v-card-title>
  </v-card>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';

import { useYardTask } from '@/composables';
import { DatabaseEntities, type IAssetContainer, type ISpot } from '@nova/core';
import { isTaskCreated } from '@satellite/helpers/yardTaskUtils';

import type { CheckTaskViewData } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

const task = toRef(props, 'task');

const { getRelatedEntity, getRelatedObject } = useYardTask({ initialTask: task });

const display = computed(() => {
  const relatedEntity = getRelatedEntity();
  switch (relatedEntity) {
    case DatabaseEntities.SpotArea:
      return {
        icon: 'mdi-map-marker-outline',
        label: 'Area Name Placeholder',
        chip: 'AREA',
        button: {
          label: 'Update Inventory',
          disabled: isTaskCreated(task.value),
          appendIcon: 'mdi-arrow-right'
        }
      };
    case DatabaseEntities.Spot:
      const spot = getRelatedObject() as ISpot | null;
      if (!spot) {
        return null;
      }
      return {
        icon: 'mdi-map-marker-outline',
        label: `Area Name Placeholder - ${spot.code}`,
        chip: 'SPOT',
        button: {
          label: 'Update Spot',
          disabled: isTaskCreated(task.value),
          appendIcon: 'mdi-chevron-down'
        }
      };
    case DatabaseEntities.AssetContainer:
      const asset = getRelatedObject() as IAssetContainer | null;
      if (!asset) {
        return null;
      }
      return {
        icon: 'mdi-truck-trailer',
        label: asset.code || 'N/A',
        chip: asset.type?.toUpperCase() || '',
        subIcon: 'mdi-map-marker-outline',
        subLabel: 'Area Name Placeholder - Spot Code Placeholder',
        button: {
          label: 'Update Asset',
          disabled: isTaskCreated(task.value),
          appendIcon: 'mdi-chevron-down'
        }
      };
    default:
      return null;
  }
});
</script>

<style scoped lang="scss">
.v-card-title {
  white-space: break-spaces;
}
</style>
